/**
 * Sector operations — Stays Lost & Found Client Service (R39 K7.a & K7.b, SO-P115).
 * Wrappers for lost & found Cloud Functions and real-time listeners.
 */

import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  limit,
  query,
  where,
  type QueryConstraint,
  type Unsubscribe,
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { firestoreDb, firebaseStorage, firebaseAuth } from './firebase'
import { callable } from './callable'
import { getApprovalsFunctions } from './approvalsApp'
import type { OpsSubscriptionAccessOptions } from './opsRostersService'
import {
  ALLOWED_PHOTO_MIME_TYPES,
  MAX_PHOTO_BYTES,
  type UploadRecordPhotoResult,
} from './opsRecordPhotosService'
import { getNlsString } from '../nls/nlsRuntime'
import {
  DEFAULT_HOLD_DAYS,
  DEFAULT_VALUABLE_HOLD_DAYS,
  buildLostFoundSettingsKey,
} from '../../functions-common/src/ops/lostFound'
import type {
  CancelLostFoundClaimInput,
  CompleteLostFoundReturnInput,
  DescribeLostClaimInput,
  GetLostClaimLinkInfoInput,
  GetLostFoundClaimantInput,
  LogFoundItemInput,
  LostFoundCategory,
  LostFoundClaimDoc,
  LostFoundClaimPiiDoc,
  LostFoundDeskVerdict,
  LostFoundEventDoc,
  LostFoundItemDoc,
  LostFoundItemStatus,
  LostFoundSettingsDoc,
  MoveFoundItemBinInput,
  OpenLostClaimInput,
  PrepareLostClaimOtpInput,
  ProposeLostFoundDisposalInput,
  RecordFoundItemPhotoInput,
  ReissueLostFoundClaimLinkInput,
  SetLostFoundSettingsInput,
  SignLostFoundDisposalInput,
  StartLostFoundClaimInput,
  VerifyLostFoundClaimInput,
  WithdrawLostFoundDisposalInput,
} from '../types/ops'

// ---------------------------------------------------------------------------
// 1. Subscriptions & Direct Reads
// ---------------------------------------------------------------------------

export function subscribeToLostFoundItems(
  workspaceId: string,
  options: { propertyId?: string; status?: LostFoundItemStatus },
  callback: (items: LostFoundItemDoc[]) => void,
  onError?: (error: Error) => void,
  accessScope?: OpsSubscriptionAccessOptions,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }

  const itemsRef = collection(firestoreDb, 'ops_lf_items')
  const baseConstraints: QueryConstraint[] = [where('workspaceId', '==', workspaceId)]
  if (options.propertyId) {
    baseConstraints.push(where('propertyId', '==', options.propertyId))
  }
  if (options.status) {
    baseConstraints.push(where('status', '==', options.status))
  }

  if (accessScope?.isRestricted === true) {
    const callerUid = accessScope.callerUid?.trim()
    if (!callerUid) {
      onError?.(new Error('callerUid is required for restricted subscribeToLostFoundItems'))
      return () => undefined
    }

    const qNull = query(itemsRef, ...baseConstraints, where('readAccess', '==', null))
    const qUid = query(itemsRef, ...baseConstraints, where('readAccess', 'array-contains', callerUid))

    let itemsNull: LostFoundItemDoc[] = []
    let itemsUid: LostFoundItemDoc[] = []

    const mergeAndEmit = () => {
      const map = new Map<string, LostFoundItemDoc>()
      for (const it of itemsNull) map.set(it.id, it)
      for (const it of itemsUid) map.set(it.id, it)
      const list = Array.from(map.values())
      list.sort((a, b) => b.foundAt - a.foundAt)
      callback(list)
    }

    const unsubNull = onSnapshot(
      qNull,
      (snap) => {
        itemsNull = snap.docs.map((d) => ({ ...d.data(), id: d.id }) as LostFoundItemDoc)
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )

    const unsubUid = onSnapshot(
      qUid,
      (snap) => {
        itemsUid = snap.docs.map((d) => ({ ...d.data(), id: d.id }) as LostFoundItemDoc)
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )

    return () => {
      unsubNull()
      unsubUid()
    }
  }

  const q = query(itemsRef, ...baseConstraints)
  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ ...d.data(), id: d.id }) as LostFoundItemDoc)
      items.sort((a, b) => b.foundAt - a.foundAt)
      callback(items)
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  )
}

export function subscribeToLostFoundItemEvents(
  itemId: string,
  callback: (events: LostFoundEventDoc[]) => void,
  onError?: (error: Error) => void,
  maxEvents: number = 100,
): Unsubscribe {
  if (!itemId) {
    callback([])
    return () => undefined
  }

  const eventsRef = collection(firestoreDb, 'ops_lf_items', itemId, 'events')
  const q = query(eventsRef, orderBy('at', 'desc'), limit(maxEvents))

  return onSnapshot(
    q,
    (snap) => {
      const events = snap.docs.map((d) => ({ ...d.data(), id: d.id }) as LostFoundEventDoc)
      callback(events)
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  )
}

export function subscribeToLostFoundClaims(
  workspaceId: string,
  options: { itemId?: string },
  callback: (claims: LostFoundClaimDoc[]) => void,
  onError?: (error: Error) => void,
  accessScope?: OpsSubscriptionAccessOptions,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }

  const claimsRef = collection(firestoreDb, 'ops_lf_claims')
  const baseConstraints: QueryConstraint[] = [where('workspaceId', '==', workspaceId)]
  if (options.itemId) {
    baseConstraints.push(where('itemId', '==', options.itemId))
  }

  const handleErr = (err: unknown) => {
    // Permission error shows nothing rather than crashing
    callback([])
    if (onError && err instanceof Error) {
      onError(err)
    }
  }

  if (accessScope?.isRestricted === true) {
    const callerUid = accessScope.callerUid?.trim()
    if (!callerUid) {
      onError?.(new Error('callerUid is required for restricted subscribeToLostFoundClaims'))
      return () => undefined
    }

    const qNull = query(claimsRef, ...baseConstraints, where('readAccess', '==', null))
    const qUid = query(claimsRef, ...baseConstraints, where('readAccess', 'array-contains', callerUid))

    let claimsNull: LostFoundClaimDoc[] = []
    let claimsUid: LostFoundClaimDoc[] = []

    const mergeAndEmit = () => {
      const map = new Map<string, LostFoundClaimDoc>()
      for (const c of claimsNull) map.set(c.id, c)
      for (const c of claimsUid) map.set(c.id, c)
      callback(Array.from(map.values()))
    }

    const unsubNull = onSnapshot(
      qNull,
      (snap) => {
        claimsNull = snap.docs.map((d) => ({ ...d.data(), id: d.id }) as LostFoundClaimDoc)
        mergeAndEmit()
      },
      handleErr,
    )

    const unsubUid = onSnapshot(
      qUid,
      (snap) => {
        claimsUid = snap.docs.map((d) => ({ ...d.data(), id: d.id }) as LostFoundClaimDoc)
        mergeAndEmit()
      },
      handleErr,
    )

    return () => {
      unsubNull()
      unsubUid()
    }
  }

  const q = query(claimsRef, ...baseConstraints)
  return onSnapshot(
    q,
    (snap) => {
      const claims = snap.docs.map((d) => ({ ...d.data(), id: d.id }) as LostFoundClaimDoc)
      callback(claims)
    },
    handleErr,
  )
}

export async function getLostFoundSettings(
  workspaceId: string,
  propertyId: string,
): Promise<LostFoundSettingsDoc> {
  if (!workspaceId || !propertyId) {
    return {
      workspaceId: workspaceId || '',
      propertyId: propertyId || '',
      holdDays: DEFAULT_HOLD_DAYS,
      valuableHoldDays: DEFAULT_VALUABLE_HOLD_DAYS,
      version: 0,
    }
  }

  const key = buildLostFoundSettingsKey(workspaceId, propertyId)
  const docRef = doc(firestoreDb, 'ops_lf_settings', key)
  const snap = await getDoc(docRef)

  if (!snap.exists()) {
    return {
      workspaceId,
      propertyId,
      holdDays: DEFAULT_HOLD_DAYS,
      valuableHoldDays: DEFAULT_VALUABLE_HOLD_DAYS,
      version: 0,
    }
  }

  return snap.data() as LostFoundSettingsDoc
}

const EXTENSION_MAP: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/heic': 'heic',
}

function getSafeExtension(file: File): string {
  const parts = file.name.split('.')
  if (parts.length > 1) {
    const ext = parts.pop()!.toLowerCase().replace(/[^a-z0-9]/g, '')
    if (['jpg', 'jpeg', 'png', 'webp', 'heic'].includes(ext)) {
      return ext === 'jpeg' ? 'jpg' : ext
    }
  }
  return EXTENSION_MAP[file.type] || 'jpg'
}

export async function uploadLostFoundPhoto(
  workspaceId: string,
  itemId: string,
  file: File,
): Promise<UploadRecordPhotoResult> {
  if (!workspaceId) throw new Error('workspaceId is required.')
  if (!itemId) throw new Error('itemId is required.')

  if (!ALLOWED_PHOTO_MIME_TYPES.includes(file.type as (typeof ALLOWED_PHOTO_MIME_TYPES)[number])) {
    throw new Error(getNlsString('NLSOpsPhotoInvalidType'))
  }

  if (file.size > MAX_PHOTO_BYTES) {
    throw new Error(getNlsString('NLSOpsPhotoTooLarge'))
  }

  const uid = firebaseAuth.currentUser?.uid
  if (!uid) {
    throw new Error('Authentication required for photo upload.')
  }

  const ext = getSafeExtension(file)
  const randomSuffix = Math.random().toString(36).slice(2, 10)
  const fileId = `${randomSuffix}_${Date.now()}.${ext}`
  const path = `workspaces/${workspaceId}/ops_lf_items/${itemId}/${uid}/${fileId}`

  const storageRef = ref(firebaseStorage, path)
  await uploadBytes(storageRef, file, {
    contentType: file.type,
  })

  const downloadUrl = await getDownloadURL(storageRef)

  const opId =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `op_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

  await recordFoundItemPhoto({
    itemId,
    path,
    opId,
  })

  return { path, downloadUrl }
}

export async function getLostFoundPhotoUrl(path: string): Promise<string> {
  if (!path) return ''
  const storageRef = ref(firebaseStorage, path)
  return getDownloadURL(storageRef)
}

// ---------------------------------------------------------------------------
// 2. Callables
// ---------------------------------------------------------------------------

export async function logFoundItem(
  input: LogFoundItemInput,
): Promise<{ item: LostFoundItemDoc }> {
  return callable<LogFoundItemInput, { item: LostFoundItemDoc }>('logFoundItem', input)
}

export async function recordFoundItemPhoto(
  input: RecordFoundItemPhotoInput,
): Promise<{ success: boolean; item: LostFoundItemDoc }> {
  return callable<RecordFoundItemPhotoInput, { success: boolean; item: LostFoundItemDoc }>(
    'recordFoundItemPhoto',
    input,
  )
}

export async function moveFoundItemBin(
  input: MoveFoundItemBinInput,
): Promise<{ success: boolean; item: LostFoundItemDoc }> {
  return callable<MoveFoundItemBinInput, { success: boolean; item: LostFoundItemDoc }>(
    'moveFoundItemBin',
    input,
  )
}

export async function startLostFoundClaim(
  input: StartLostFoundClaimInput,
): Promise<{ claimId: string; token: string; send: { mode: 'MANUAL' } }> {
  return callable<
    StartLostFoundClaimInput,
    { claimId: string; token: string; send: { mode: 'MANUAL' } }
  >('startLostFoundClaim', input)
}

export async function reissueLostFoundClaimLink(
  input: ReissueLostFoundClaimLinkInput,
): Promise<{ claimId: string; token: string; send: { mode: 'MANUAL' } }> {
  return callable<
    ReissueLostFoundClaimLinkInput,
    { claimId: string; token: string; send: { mode: 'MANUAL' } }
  >('reissueLostFoundClaimLink', input)
}

export async function cancelLostFoundClaim(
  input: CancelLostFoundClaimInput,
): Promise<{ success: boolean }> {
  return callable<CancelLostFoundClaimInput, { success: boolean }>('cancelLostFoundClaim', input)
}

export async function verifyLostFoundClaim(
  input: VerifyLostFoundClaimInput,
): Promise<{ success: boolean; verdict: LostFoundDeskVerdict }> {
  return callable<VerifyLostFoundClaimInput, { success: boolean; verdict: LostFoundDeskVerdict }>(
    'verifyLostFoundClaim',
    input,
  )
}

export async function completeLostFoundReturn(
  input: CompleteLostFoundReturnInput,
): Promise<{ success: boolean }> {
  return callable<CompleteLostFoundReturnInput, { success: boolean }>(
    'completeLostFoundReturn',
    input,
  )
}

export async function proposeLostFoundDisposal(
  input: ProposeLostFoundDisposalInput,
): Promise<{ success: boolean; item: LostFoundItemDoc }> {
  return callable<ProposeLostFoundDisposalInput, { success: boolean; item: LostFoundItemDoc }>(
    'proposeLostFoundDisposal',
    input,
  )
}

export async function signLostFoundDisposal(
  input: SignLostFoundDisposalInput,
): Promise<{ success: boolean; item: LostFoundItemDoc }> {
  return callable<SignLostFoundDisposalInput, { success: boolean; item: LostFoundItemDoc }>(
    'signLostFoundDisposal',
    input,
  )
}

export async function withdrawLostFoundDisposal(
  input: WithdrawLostFoundDisposalInput,
): Promise<{ success: boolean; item: LostFoundItemDoc }> {
  return callable<WithdrawLostFoundDisposalInput, { success: boolean; item: LostFoundItemDoc }>(
    'withdrawLostFoundDisposal',
    input,
  )
}

export async function setLostFoundSettings(
  input: SetLostFoundSettingsInput,
): Promise<{ settings: LostFoundSettingsDoc }> {
  return callable<SetLostFoundSettingsInput, { settings: LostFoundSettingsDoc }>(
    'setLostFoundSettings',
    input,
  )
}

export async function getLostFoundClaimant(
  input: GetLostFoundClaimantInput,
): Promise<{ claimant: LostFoundClaimPiiDoc }> {
  return callable<GetLostFoundClaimantInput, { claimant: LostFoundClaimPiiDoc }>(
    'getLostFoundClaimant',
    input,
  )
}

export async function getLostClaimLinkInfo(
  input: GetLostClaimLinkInfoInput,
): Promise<{
  propertyName: string
  category: LostFoundCategory
  phoneMasked: string
  linkExpiresAt: number
}> {
  return callable<
    GetLostClaimLinkInfoInput,
    { propertyName: string; category: LostFoundCategory; phoneMasked: string; linkExpiresAt: number }
  >('getLostClaimLinkInfo', input, { functions: getApprovalsFunctions() })
}

export async function prepareLostClaimOtp(
  input: PrepareLostClaimOtpInput,
): Promise<{ success: boolean; attemptsRemaining: number }> {
  return callable<PrepareLostClaimOtpInput, { success: boolean; attemptsRemaining: number }>(
    'prepareLostClaimOtp',
    input,
    { functions: getApprovalsFunctions() },
  )
}

export async function openLostClaim(
  input: OpenLostClaimInput,
): Promise<{
  claim: {
    id: string
    status: string
    phoneMasked: string
    linkExpiresAt: number
    returnMode?: string
    guestDescription?: string
  }
  category: LostFoundCategory
  propertyName: string
}> {
  return callable<
    OpenLostClaimInput,
    {
      claim: {
        id: string
        status: string
        phoneMasked: string
        linkExpiresAt: number
        returnMode?: string
        guestDescription?: string
      }
      category: LostFoundCategory
      propertyName: string
    }
  >('openLostClaim', input, { functions: getApprovalsFunctions() })
}

export async function describeLostClaim(
  input: DescribeLostClaimInput,
): Promise<{ success: boolean }> {
  return callable<DescribeLostClaimInput, { success: boolean }>(
    'describeLostClaim',
    input,
    { functions: getApprovalsFunctions() },
  )
}
